import { WHATSAPP_API_CHANNEL, toPersistedChannel } from "./connectionChannel";

const OAUTH_STATE_PREFIX = "whatsapp_business_auth";

/** Meta postMessage origins for Embedded Signup session info. */
export const EMBEDDED_SIGNUP_MESSAGE_ORIGINS = new Set([
  "https://www.facebook.com",
  "https://web.facebook.com",
]);

/** Embedded Signup finish events that include WABA / phone IDs. */
export const EMBEDDED_SIGNUP_FINISH_EVENTS = new Set([
  "FINISH",
  "FINISH_ONLY_WABA",
]);

/** Wait briefly after FB.login so sessionInfo postMessage can arrive. */
export const EMBEDDED_SIGNUP_SESSION_WAIT_MS = 2500;

/** Scopes required for Cloud send + WABA discovery (OAuth fallback + Embedded Signup). */
export const WHATSAPP_CLOUD_OAUTH_SCOPES =
  "whatsapp_business_management,whatsapp_business_messaging,business_management";

export function getFacebookAppId() {
  const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
  if (!appId) {
    throw new Error("REACT_APP_FACEBOOK_APP_ID no está configurado");
  }
  return appId;
}

export function getFacebookApiVersion() {
  return process.env.REACT_APP_FACEBOOK_API_VERSION || "v23.0";
}

export function getEmbeddedSignupConfigId() {
  return process.env.REACT_APP_FACEBOOK_EMBEDDED_SIGNUP_CONFIG_ID || "";
}

export function getOAuthRedirectUri() {
  return `${window.location.origin}/auth/facebook/callback`;
}

export function buildOAuthState(whatsappId) {
  if (whatsappId != null && whatsappId !== "") {
    return `${OAUTH_STATE_PREFIX}:${whatsappId}`;
  }
  return OAUTH_STATE_PREFIX;
}

export function parseOAuthState(state) {
  if (!state || !state.startsWith(OAUTH_STATE_PREFIX)) {
    return null;
  }
  const [, rawId] = state.split(":");
  const whatsappId = rawId ? Number.parseInt(rawId, 10) : undefined;
  return {
    whatsappId: Number.isFinite(whatsappId) ? whatsappId : undefined,
  };
}

export function isValidOAuthState(state) {
  return Boolean(parseOAuthState(state));
}

/**
 * Parse Meta WA_EMBEDDED_SIGNUP window message (sessionInfoVersion 2+).
 * Returns null for non-JSON or unrelated messages.
 */
export function parseEmbeddedSignupMessage(event) {
  if (!event?.origin || !EMBEDDED_SIGNUP_MESSAGE_ORIGINS.has(event.origin)) {
    return null;
  }
  if (typeof event.data !== "string") {
    return null;
  }

  let payload;
  try {
    payload = JSON.parse(event.data);
  } catch {
    return null;
  }

  if (payload?.type !== "WA_EMBEDDED_SIGNUP") {
    return null;
  }

  const data = payload.data ?? {};
  const wabaId =
    data.waba_id ??
    (Array.isArray(data.waba_ids) && data.waba_ids.length > 0
      ? data.waba_ids[0]
      : undefined);

  return {
    event: payload.event,
    wabaId,
    phoneNumberId: data.phone_number_id,
    businessId: data.business_id,
    currentStep: data.current_step,
  };
}

function loadFacebookSdk(appId, apiVersion) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    const existing = document.getElementById("facebook-jssdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.FB));
      existing.addEventListener("error", reject);
      return;
    }

    window.fbAsyncInit = function fbAsyncInit() {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: apiVersion,
      });
      resolve(window.FB);
    };

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.onerror = () => reject(new Error("facebook_sdk_load_failed"));
    document.body.appendChild(script);
  });
}

function waitForEmbeddedSignupSession(sessionPromise, timeoutMs) {
  return Promise.race([
    sessionPromise,
    new Promise((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);
}

function createEmbeddedSignupSessionListener() {
  let finishResolve;
  let finishReject;

  const sessionPromise = new Promise((resolve, reject) => {
    finishResolve = resolve;
    finishReject = reject;
  });

  const handleMessage = (event) => {
    const parsed = parseEmbeddedSignupMessage(event);
    if (!parsed) {
      return;
    }

    if (parsed.event === "CANCEL") {
      finishReject(new Error("signup_canceled"));
      return;
    }

    if (EMBEDDED_SIGNUP_FINISH_EVENTS.has(parsed.event)) {
      finishResolve(parsed);
    }
  };

  return { sessionPromise, handleMessage };
}

function launchEmbeddedSignupPopup({ whatsappId }) {
  const configId = getEmbeddedSignupConfigId();
  if (!configId) {
    return Promise.reject(new Error("embedded_signup_config_missing"));
  }

  const appId = getFacebookAppId();
  const apiVersion = getFacebookApiVersion();
  const { sessionPromise, handleMessage } = createEmbeddedSignupSessionListener();

  window.addEventListener("message", handleMessage);

  const cleanup = () => {
    window.removeEventListener("message", handleMessage);
  };

  return loadFacebookSdk(appId, apiVersion).then(
    (FB) =>
      new Promise((resolve, reject) => {
        FB.login(
          (response) => {
            const finalize = async () => {
              cleanup();
              const code = response?.authResponse?.code;
              if (!code) {
                reject(new Error("signup_canceled"));
                return;
              }

              let session = null;
              try {
                session = await waitForEmbeddedSignupSession(
                  sessionPromise,
                  EMBEDDED_SIGNUP_SESSION_WAIT_MS,
                );
              } catch (sessionError) {
                reject(sessionError);
                return;
              }

              resolve({
                code,
                whatsappId,
                channel: WHATSAPP_API_CHANNEL,
                embeddedSignup: true,
                businessAccountId: session?.wabaId,
                phoneNumberId: session?.phoneNumberId,
              });
            };

            finalize().catch(reject);
          },
          {
            config_id: configId,
            response_type: "code",
            override_default_response_type: true,
            scope: WHATSAPP_CLOUD_OAUTH_SCOPES,
            extras: {
              setup: {},
              featureType: "",
              sessionInfoVersion: 2,
            },
          },
        );
      }),
  );
}

function launchOAuthSignupPopup({ whatsappId }) {
  const appId = getFacebookAppId();
  const apiVersion = getFacebookApiVersion();
  const redirectUri = getOAuthRedirectUri();
  const state = buildOAuthState(whatsappId);

  const authUrl =
    `https://www.facebook.com/${apiVersion}/dialog/oauth?` +
    `client_id=${encodeURIComponent(appId)}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `scope=${encodeURIComponent(WHATSAPP_CLOUD_OAUTH_SCOPES)}&` +
    `response_type=code&` +
    `state=${encodeURIComponent(state)}`;

  return new Promise((resolve, reject) => {
    const popup = window.open(
      authUrl,
      "facebook_whatsapp_cloud_signup",
      "width=600,height=700,scrollbars=yes,resizable=yes",
    );

    if (!popup) {
      reject(new Error("popup_blocked"));
      return;
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const { type, connections, error } = event.data || {};
      if (type === "FACEBOOK_AUTH_SUCCESS") {
        cleanup();
        resolve({ connections, channel: WHATSAPP_API_CHANNEL });
        return;
      }
      if (type === "FACEBOOK_AUTH_ERROR") {
        cleanup();
        reject(new Error(error || "facebook_auth_failed"));
      }
    };

    const cleanup = () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(checkClosed);
    };

    window.addEventListener("message", handleMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("signup_canceled"));
      }
    }, 500);
  });
}

/**
 * Opens Meta Embedded Signup (FB.login + config_id) or OAuth popup fallback.
 * Embedded path returns session IDs + code for POST /whatsapp/business.
 * OAuth path resolves when FacebookCallback posts FACEBOOK_AUTH_SUCCESS.
 */
export async function launchWhatsAppCloudSignup({ whatsappId } = {}) {
  const configId = getEmbeddedSignupConfigId();
  if (configId) {
    return launchEmbeddedSignupPopup({ whatsappId });
  }
  return launchOAuthSignupPopup({ whatsappId });
}

export function buildBusinessConnectPayload({
  code,
  whatsappId,
  channel,
  name,
  embeddedSignup,
  businessAccountId,
  phoneNumberId,
}) {
  return {
    code,
    whatsappId,
    channel: toPersistedChannel(channel) || WHATSAPP_API_CHANNEL,
    name,
    ...(businessAccountId ? { businessAccountId } : {}),
    ...(phoneNumberId ? { phoneNumberId } : {}),
    ...(embeddedSignup ? { embeddedSignup: true } : {}),
  };
}
